import type { ExamAmendment } from "$global-providers/rxdb-provider/collections/epocus-exam";
import { Box, Button, styled, Typography } from "@material-ui/core";
import { format } from "date-fns";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../../../_lib/epocus-palette";
import { WorksheetSection } from "../../../../../_lib/worksheets/worksheet-primitives";
import type { ReportVersion } from "./report-amendment";

const Entry = styled(Box)({
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  padding: "8px 0",
});

const EntryTitle = styled(Typography)({
  fontSize: rem(13),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const EntryText = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_PRIMARY,
  whiteSpace: "pre-wrap",
});

const at = (iso: string) => format(new Date(iso), "d MMM yyyy, HH:mm");

export const ReportHistory: FC<{
  signedAt: string;
  amendments: readonly ExamAmendment[];
  versions: readonly ReportVersion[];
  viewing: string;
  onView: (key: string) => void;
}> = ({ signedAt, amendments, versions, viewing, onView }) => (
  <WorksheetSection label="Report history">
    <Entry style={{ borderTop: "none" }}>
      <EntryTitle>Signed · {at(signedAt)}</EntryTitle>
    </Entry>
    {amendments.map((amendment) => (
      <Entry key={amendment.id}>
        <EntryTitle>
          {amendment.kind === "AMEND" ? "Amended" : "Confirmed as it stands"} ·{" "}
          {at(amendment.signedAt)}
        </EntryTitle>
        <EntryText>
          {amendment.kind === "AMEND" ? "Reason" : "Note"}: {amendment.reason}
        </EntryText>
      </Entry>
    ))}
    {versions.length > 0 && (
      <Box
        role="radiogroup"
        aria-label="Report version"
        display="flex"
        flexWrap="wrap"
        style={{ gap: 8, marginTop: 8 }}
      >
        {versions.map((version) => {
          const selected = version.key === viewing;
          return (
            <Button
              key={version.key}
              role="radio"
              aria-checked={selected}
              size="small"
              variant="outlined"
              onClick={() => onView(version.key)}
              style={{
                textTransform: "none",
                fontSize: rem(13),
                borderColor: selected ? EPOCUS_PRIMARY : EPOCUS_BORDER,
                backgroundColor: selected ? EPOCUS_SELECTED_BG : undefined,
                color: selected ? EPOCUS_PRIMARY : EPOCUS_TEXT_SECONDARY,
              }}
            >
              {version.label}
            </Button>
          );
        })}
      </Box>
    )}
  </WorksheetSection>
);
